import { Platform } from 'react-native';
import { API_URLS } from '../api/config';
import * as FileSystem from 'expo-file-system';
import { Audio } from 'expo-av';
import { Howl } from 'howler';
import { createBaseAudioContext, base64ToArrayBuffer } from '../utils/audioUtils';

// Initialize Audio for mobile
async function initializeAudio() {
  if (Platform.OS !== 'web') {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        playsInSilentModeIOS: true,
        staysActiveInBackground: true,
        shouldDuckAndroid: true,
        playThroughEarpieceAndroid: false,
      });
    } catch (error) {
      console.error('[TTS] Error initializing mobile audio:', error);
    }
  }
}

// Call initialization when the module loads
initializeAudio();

// Configuration
const TTS_CONFIG = {
  CHUNK_SILENCE_MS: 450, // Silence between chunks in milliseconds
  MAX_CHUNK_LENGTH: 150  // Maximum chunk length in characters
};

// State management
let currentHowl: Howl | null = null;
let currentSound: Audio.Sound | null = null;
let isPlaying = false;
let isPaused = false;
let audioQueue: { content: string; ready: boolean }[] = [];
let currentChunkIndex = 0;

// Token management
let cachedToken: string | null = null;
let tokenExpiryTime: number | null = null;
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry

const ttsTokenUrl = API_URLS.ttsToken;
const ttsServiceUrl = API_URLS.googleTTS;

interface TTSOptions {
  voice: string;
  speed: number;
  audioFormat: string;
  pitch: number;
}

function cleanTextForTTS(text: string): string {
  return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function removeEmptyChunks(chunks: string[]): string[] {
  return chunks.filter(chunk => {
    const cleaned = cleanTextForTTS(chunk);
    return cleaned.length > 0 && /\S/.test(cleaned);
  });
}

function chunkByPunctuation(text: string, maxChunkLength = TTS_CONFIG.MAX_CHUNK_LENGTH) {
  const cleanedText = cleanTextForTTS(text);
  const chunks: string[] = [];
  
  // Split by major punctuation first (periods, exclamation marks, question marks)
  const sentences = cleanedText.split(/(?<=[.!?])\s+/);
  
  for (const sentence of sentences) {
    if (sentence.length <= maxChunkLength) {
      chunks.push(sentence.trim());
      continue;
    }
    
    // For longer sentences, split by secondary punctuation (commas, semicolons, colons)
    const phrases = sentence.split(/(?<=[,;:])\s+/);
    let currentChunk = '';
    
    for (const phrase of phrases) {
      if ((currentChunk + phrase).length > maxChunkLength) {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
          currentChunk = '';
        }
        
        // If a single phrase is still too long, split by natural word boundaries
        if (phrase.length > maxChunkLength) {
          const words = phrase.split(/\s+/);
          for (const word of words) {
            if ((currentChunk + word).length > maxChunkLength) {
              if (currentChunk) {
                chunks.push(currentChunk.trim());
                currentChunk = '';
              }
            }
            currentChunk += word + ' ';
          }
        } else {
          currentChunk = phrase + ' ';
        }
      } else {
        currentChunk += phrase + ' ';
      }
    }
    
    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }
  }

  return removeEmptyChunks(chunks);
}

export async function synthesizeChunk(
  text: string,
  token: string,
  language: string,
  options: TTSOptions
): Promise<string> {
  try {
    const normalizedLanguage = language.toLowerCase().replace('-mx', '-us');
    
    const request = {
      input: { text: cleanTextForTTS(text) },
      voice: {
        languageCode: normalizedLanguage,
        name: options.voice,
      },
      audioConfig: {
        audioEncoding: 'MP3',
        speakingRate: options.speed || 1.0,
        pitch: options.pitch || 0.0,
        volumeGainDb: 0.0
      }
    };

    return fetch(ttsServiceUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (!data.audioContent) {
        throw new Error('No audio content in response');
      }
      return data.audioContent;
    });
  } catch (error) {
    console.error('Error in synthesizeChunk:', error);
    throw error;
  }
}

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let gainNode: GainNode | null = null;

function setupAudioContext() {
  if (!audioContext) {
    audioContext = createBaseAudioContext();
    analyser = audioContext.createAnalyser();
    gainNode = audioContext.createGain();
    
    analyser.fftSize = 256;
    gainNode.connect(audioContext.destination);
    analyser.connect(gainNode);
  }
  return { audioContext, analyser, gainNode };
}

export async function playAudio(audioBase64: string): Promise<void> {
  if (Platform.OS === 'web') {
    return playAudioWithHowler(audioBase64);
  } else {
    return playMobileAudio(audioBase64);
  }
}

async function playAudioWithHowler(audioBase64: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (currentHowl) {
        currentHowl.stop();
        currentHowl.unload();
        currentHowl = null;
      }

      let audioContext: AudioContext | null = null;
      let analyser: AnalyserNode | null = null;
      let animationFrame: number | null = null;

      // Only setup visualization on web platform
      if (Platform.OS === 'web') {
        audioContext = createBaseAudioContext();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        
        // Create buffer source
        const arrayBuffer = base64ToArrayBuffer(audioBase64);
        audioContext.decodeAudioData(arrayBuffer, (audioBuffer) => {
          const source = audioContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(analyser);
          analyser.connect(audioContext.destination);

          // Start visualization
          const animate = () => {
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(dataArray);
            
            if (window.visualizerCallback) {
              window.visualizerCallback(dataArray);
            }
            
            animationFrame = requestAnimationFrame(animate);
          };
          animate();

          source.start(0);
          source.onended = () => {
            if (animationFrame) {
              cancelAnimationFrame(animationFrame);
            }
            if (audioContext) {
              audioContext.close().catch(console.error);
            }
            resolve();
          };
        }, (error) => {
          console.error('Error decoding audio:', error);
          reject(error);
        });
      }

      const howl = new Howl({
        src: [`data:audio/mp3;base64,${audioBase64}`],
        format: ['mp3'],
        html5: true,
        onend: () => {
          if (animationFrame) {
            cancelAnimationFrame(animationFrame);
          }
          if (audioContext) {
            audioContext.close().catch(console.error);
          }
          resolve();
        },
        onloaderror: (id, error) => {
          if (animationFrame) {
            cancelAnimationFrame(animationFrame);
          }
          if (audioContext) {
            audioContext.close().catch(console.error);
          }
          reject(error);
        },
        onplayerror: (id, error) => {
          if (animationFrame) {
            cancelAnimationFrame(animationFrame);
          }
          if (audioContext) {
            audioContext.close().catch(console.error);
          }
          reject(error);
        }
      });

      currentHowl = howl;
      if (!Platform.OS === 'web') {
        howl.play();
      }
    } catch (error) {
      reject(error);
    }
  });
}

async function playMobileAudio(audioBase64: string): Promise<void> {
  return new Promise(async (resolve, reject) => {
    try {
      if (currentSound) {
        await currentSound.unloadAsync();
        currentSound = null;
      }

      const { sound } = await Audio.Sound.createAsync(
        { uri: `data:audio/mp3;base64,${audioBase64}` },
        { shouldPlay: true }
      );

      currentSound = sound;
      
      // Simulated visualization for mobile
      let animationFrame: number | null = null;
      const simulateVisualization = () => {
        if (window.visualizerCallback) {
          const dataArray = new Uint8Array(128);
          for (let i = 0; i < dataArray.length; i++) {
            dataArray[i] = Math.random() * 128;
          }
          window.visualizerCallback(dataArray);
        }
        animationFrame = requestAnimationFrame(simulateVisualization);
      };
      
      simulateVisualization();

      sound.setOnPlaybackStatusUpdate(status => {
        if (status.didJustFinish) {
          if (animationFrame) {
            cancelAnimationFrame(animationFrame);
          }
          resolve();
        }
      });
    } catch (error) {
      reject(error);
    }
  });
}

// Cache for preloaded chunks
let audioChunkCache: { [key: string]: string } = {};

async function preloadChunk(
  chunk: string,
  token: string,
  language: string,
  options: TTSOptions
): Promise<void> {
  try {
    const audioContent = await synthesizeChunk(chunk, token, language, options);
    if (audioContent) {
      audioChunkCache[chunk] = audioContent;
    }
  } catch (error) {
    console.error('Error preloading chunk:', error);
  }
}

// Add delay between chunks
const addSilence = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export async function synthesizeRealTimeSpeech(
  text: string,
  token: string = '',
  language: string,
  options: TTSOptions = {
    voice: 'en-US-Neural2-F',
    speed: 1.0,
    pitch: 0.0
  },
  onComplete?: () => void
): Promise<void> {
  try {
    if (!token) {
      token = await getTTSToken();
    }

    isPlaying = true;
    isPaused = false;
    audioChunkCache = {}; // Clear cache at start

    const chunks = chunkByPunctuation(text);
    console.log('Chunks to play:', chunks.length);

    for (let i = 0; i < chunks.length; i++) {
      if (!isPlaying || isPaused) break;

      const currentChunk = chunks[i];
      console.log(`Playing chunk ${i + 1}/${chunks.length}:`, currentChunk);
      
      // Start preloading next chunk if it exists
      if (i + 1 < chunks.length) {
        const nextChunk = chunks[i + 1];
        preloadChunk(nextChunk, token, language, options).catch(console.error);
      }

      try {
        // Check if chunk is in cache
        let audioContent = audioChunkCache[currentChunk];
        if (!audioContent) {
          audioContent = await synthesizeChunk(currentChunk, token, language, options);
        } else {
          delete audioChunkCache[currentChunk]; // Remove from cache after use
        }

        if (audioContent && isPlaying && !isPaused) {
          await playAudio(audioContent);
          
          // Add silence between chunks, but not after the last chunk
          if (i < chunks.length - 1) {
            await addSilence(TTS_CONFIG.CHUNK_SILENCE_MS);
          }
        }
      } catch (error) {
        console.error(`Error playing chunk ${i + 1}:`, error);
        throw error;
      }
    }
  } catch (error) {
    console.error('Error in synthesizeRealTimeSpeech:', error);
    throw error;
  } finally {
    isPlaying = false;
    audioChunkCache = {}; // Clear cache when done
    if (onComplete) {
      onComplete();
    }
  }
}

export async function pauseTTS() {
  try {
    if (currentHowl) {
      currentHowl.pause();
      isPaused = true;
      isPlaying = false;
    } else if (currentSound) {
      await currentSound.pauseAsync();
      isPaused = true;
      isPlaying = false;
    }
  } catch (error) {
    console.error('Error pausing TTS:', error);
  }
}

export async function resumeTTS() {
  try {
    if (currentHowl) {
      currentHowl.play();
      isPaused = false;
      isPlaying = true;
    } else if (currentSound) {
      await currentSound.playAsync();
      isPaused = false;
      isPlaying = true;
    }
  } catch (error) {
    console.error('Error resuming TTS:', error);
  }
}

export async function stopTTS() {
  try {
    if (currentHowl) {
      currentHowl.stop();
      currentHowl.unload();
      currentHowl = null;
    } else if (currentSound) {
      await currentSound.stopAsync();
      await currentSound.unloadAsync();
      currentSound = null;
    }
    isPlaying = false;
    isPaused = false;
  } catch (error) {
    console.error('Error stopping TTS:', error);
  }
}

export async function getTTSToken(): Promise<string> {
  if (cachedToken && tokenExpiryTime && Date.now() < tokenExpiryTime - TOKEN_REFRESH_BUFFER) {
    return cachedToken;
  }

  try {
    const response = await fetch(ttsTokenUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { error: 'Unknown error' };
      }
      throw new Error(errorData.error || `Failed to get TTS token: ${response.status}`);
    }

    const data = await response.json();
    if (!data.token) {
      throw new Error('No token received from server');
    }
    
    cachedToken = data.token;
    tokenExpiryTime = Date.now() + 60 * 60 * 1000;
    return data.token;
  } catch (error) {
    cachedToken = null;
    tokenExpiryTime = null;
    throw error;
  }
}

export async function synthesizeChatSpeech(
  text: string,
  token: string,
  language: string = 'en-US',
  options: TTSOptions
) {
  try {
    // Get audio content directly from synthesizeChunk
    const audioContent = await synthesizeChunk(text, token, language, options);
    if (!audioContent) {
      throw new Error('No audio content received');
    }
    return audioContent;
  } catch (error) {
    console.error('Error in synthesizeChatSpeech:', error);
    throw error;
  }
};